import { AxiosRequestConfigMethods } from "@/api/types";
import { brandUrl, fetchFromBrandedUrl } from "@/api/utils";
import {
  Customer,
  LeadSourceResponse,
  LeadTypesResponse,
  MarkAsRubbishRequest,
  MethodsOfContactResponse,
  MoveJourneysRequest,
  StatusesResponse,
  getJourneyStatusesResponse,
  AddBulkTagsRequest,
  BasePaginationParams,
} from "@/modules/common/dtos/index.v2";
import { EnvironmentVariables } from "@/modules/common/types";
import {
  DuplicateCustomerResponse,
  LeadSearchFilters,
} from "@/modules/leads/types";
import { Pool } from "@/modules/pools/dtos";
import { UUID } from "crypto";
import {
  GenerateReferralLinkDto,
  JourneysResponse,
  getLaunchesResponse,
} from "@/modules/leads/dtos/index.v2";
import { LeadSalesResponse } from "@/modules/lead-page/dtos";

export const getJourneys = async ({
  filters,
  pagination,
  alertPageFilter,
  organizationUIds,
}: {
  organizationUIds?: string;
  filters: LeadSearchFilters;
  pagination: {
    page: number;
    pageSize: number;
    orderBy: string;
    orderDirection: string;
  };
  alertPageFilter: {};
}) => {
  const apiUrl = brandUrl<Promise<JourneysResponse>>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/journeys`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        ...filters,
        ...pagination,
        ...alertPageFilter,
        organizationUIds,
      },
    },
  });
};

export const getMethodOfContactsV2 = async (params: {}) => {
  const apiUrl = brandUrl<MethodsOfContactResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/methods-of-contact`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        ...params,
      },
      includeOrganizationUid: true,
    },
  }) as Promise<MethodsOfContactResponse>;
};

export const getLeadTypesV2 = async (params: {}) => {
  const apiUrl = brandUrl<LeadTypesResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/lead-types`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        ...params,
      },
      includeOrganizationUid: true,
    },
  }) as Promise<LeadTypesResponse>;
};

export const getStatusesV2 = async (params: {}) => {
  const apiUrl = brandUrl<StatusesResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/statuses`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        ...params,
      },
      includeOrganizationUid: true,
    },
  }) as Promise<StatusesResponse>;
};

export const getMoveLeadStatuses = async ({
  params,
  pagination,
}: {
  params: {
    name?: string;
    leadTypeUId?: string;
    organizationUId?: string;
  };
  pagination: BasePaginationParams;
}) => {
  const apiUrl = brandUrl<StatusesResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/statuses/get-move-lead-statuses`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        ...params,
        ...pagination,
      },
    },
  }) as Promise<StatusesResponse>;
};

export const getLeadSourcesV2 = async (params: {}) => {
  const apiUrl = brandUrl<LeadSourceResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/lead-sources`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        ...params,
      },
      includeOrganizationUid: true,
    },
  }) as Promise<LeadSourceResponse>;
};

export const moveJourneys = async (data: MoveJourneysRequest) => {
  const apiUrl = brandUrl(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/journeys/move-journeys`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data: data,
    },
  });
};

export const markAsRubbish = async (data: MarkAsRubbishRequest) => {
  const apiUrl = brandUrl(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/journeys/mark-as-rubbish`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data: data,
    },
  });
};

export const getPoolJourneys = async (poolUId: UUID, leadTypeUId: UUID) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/journeys/get-pool-journeys`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        poolUId,
        leadTypeUId,
      },
    },
  });
};

export const getLaunchesV2 = () => {
  const apiUrl = brandUrl<getLaunchesResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/launches/get-filter-options`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  }) as Promise<getLaunchesResponse>;
};

export const addCustomer = async (body: Customer) => {
  const apiUrl = brandUrl<Customer>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/customers`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data: body,
    },
  }) as Promise<Customer>;
};

export const exportJourneys = async ({
  filters,
}: {
  filters: LeadSearchFilters;
}) => {
  const apiUrl = brandUrl<{
    url: string;
  }>(`${EnvironmentVariables.NEW_CRM_BASE_API_URL}/journeys/export-journeys`);

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: { ...filters },
    },
  });
};

export const exportSpecificJourneys = async ({
  journeyUIds,
}: {
  journeyUIds: UUID[];
}) => {
  const apiUrl = brandUrl<{
    url: string;
  }>(`${EnvironmentVariables.NEW_CRM_BASE_API_URL}/journeys/export-by-ids`);

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data: { journeyUIds },
    },
  });
};

export const addBulkCustomers = async (formData: FormData) => {
  const apiUrl = brandUrl(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/import-leads/bulk-upload`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      formData: true,
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  });
};

export const getUserPools = async (userUId: string | undefined) => {
  const apiUrl = brandUrl<{
    data: Pool[];
  }>(`${EnvironmentVariables.NEW_CRM_BASE_API_URL}/${userUId}/user-pools`);

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      includeSingleOrganization: true,
    },
  }) as Promise<{
    data: Pool[];
  }>;
};

export const getJourneyStatuses = (journeyUId: string | UUID) => {
  const apiUrl = brandUrl<getJourneyStatusesResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/statuses/${journeyUId}/get-journey-statuses`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  }) as Promise<{
    data: getJourneyStatusesResponse[];
  }>;
};

export const addWhatsAppUrlV2 = ({
  leadJourneyUId,
  comment,
}: {
  leadJourneyUId: string;
  comment: string;
}) => {
  const apiUrl = brandUrl(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/comments/whatsapp`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: "POST",
      data: { leadJourneyUId, comment },
    },
  });
};

export const getPartnersWithMethodOfContactV2 = async (
  params: Record<string, any>
) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/brokers/get-by-method-of-contact`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params,
    },
  });
};

export const getCommissionPaymentStatusesV2 = async (saleId: number) => {
  const apiUrl = brandUrl(
    `${EnvironmentVariables.MIDAS_API}/commissionpaymentstatusupdates`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      method: AxiosRequestConfigMethods.GET,
      isEncoded: false,
      params: { saleId: saleId },
    },
  });
};

export const getCommissionCollectionStatusesV2 = async (saleId: number) => {
  const apiUrl = brandUrl(
    `${EnvironmentVariables.MIDAS_API}/commissioncollectionstatusupdates`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      method: AxiosRequestConfigMethods.GET,
      isEncoded: false,
      params: { saleId: saleId },
    },
  });
};

export const getLeadSales = async (lead_id: string | number | undefined) => {
  const apiUrl = brandUrl<LeadSalesResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/sales/lead/${lead_id}`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      method: AxiosRequestConfigMethods.GET,
      isEncoded: false,
    },
  });
};

export const checkLeadsSalesProgress = async (
  journeyUIds: string[]
): Promise<{ journeyUId: string; customerName: string }[]> => {
  const apiUrl = brandUrl<{ journeyUId: string; customerName: string }[]>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/sales/check-leads-sales-progress`
  );
  const response = await fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      method: AxiosRequestConfigMethods.POST,
      isEncoded: false,
      data: { journeyUIds },
    },
  });
  return response?.data ?? [];
};

export const getCustomersByTelephone = async (
  organizationUId: string,
  telephone: string
) => {
  const apiUrl = brandUrl<DuplicateCustomerResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/customers/get-by-telephone`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      method: AxiosRequestConfigMethods.GET,
      isEncoded: false,
      params: { organizationUId, telephone },
    },
  }) as Promise<DuplicateCustomerResponse>;
};

export const generateReferralCode = async () => {
  const apiUrl = brandUrl(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/public-referrals/generate-referral-code`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      method: AxiosRequestConfigMethods.GET,
      isEncoded: false,
    },
  }) as Promise<GenerateReferralLinkDto>;
};

export const addBulkTags = async (data: AddBulkTagsRequest) => {
  const apiUrl = brandUrl(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/leads/bulk-add-lead-tags`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data: data,
    },
  });
};

export const bulkUpdateLeadsLocation = async (data: {
  leadUIds: string[];
  locationUId: string;
}) => {
  const apiUrl = brandUrl(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/leads/bulk-update-location`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PATCH,
      data: data,
    },
  });
};

export const bulkSendExpoQr = async (data: { journeyUIds: string[] }) => {
  const apiUrl = brandUrl(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/expo/bulk-send-qr`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data: data,
    },
  });
};

export const getPoolLeadTypes = async (poolUId: string) => {
  const apiUrl = brandUrl(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/lead-types/pool/${poolUId}`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  });
};

export const getDevelopers = async (params: { name?: string } = {}) => {
  const apiUrl = brandUrl(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/developers/lead-developers`
  );
  
 

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: params ? params : {},
    },
  });
};

export const getDeveloperUsers = async (
  developerUId?: string,
  params?: {search ?: string; excludeBlocked?: boolean }
) => {
  const apiUrl = brandUrl(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/developers/${developerUId}/users`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: params ? params : {},
    },
  });
};
